import React from "react";
import { createTheme } from "@material-ui/core/styles";
import { BrandsEnum } from "../enums";
import * as ToggleButtons from "./common/ToggleButtonCommonBase";
import * as Helpers from "./common/Helpers";
import * as DatePicker from "./common/DateTimePickerCommonBase";
import { PositionProperty, TextTransformProperty } from "csstype";

// create default theme to get breakpoints
const theme = createTheme();
const colorCard = {};

const variables = {
  common: {
    borderRadius: 0,
  },
  colors: {
    primary: {
      main: "#0F1820",
      dark: "#53565A",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#0F1820",
      dark: "#53565A",
      contrastColor: "#FF7500",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#E03C31",
    },
    info: {
      main: "#007BFF",
      light: "#DBE5F4",
    },
    text: {
      primary: "#000000",
      secondary: "#53565A",
      hint: "#75787B",
    },
    input: {
      hover: "#F8F8F8",
    },
    background: "#ECECE7",
    border: "#75787B",
    hover: "#53565A",
    placeholder: "#97999B",
    transparent: "transparent",
    divider: "#FFF",
    backgroundHeader: "#FFFFFF",
    selectedBackground: "#DEDAD4",
    hoverBorder: "rgba(15,15,15,0.6)",
    hoverButton: "#f08d5f",
    contrastColor: "#FF7500",
  },
};

function getFontFamily(mainFont: string): string {
  return [mainFont ?? "", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
}

const props = Helpers.PrepareProperties(variables);

// More properties here: https://material-ui.com/customization/default-theme/
export const polestar = {
  themeName: "Polestar",
  brand: BrandsEnum.Polestar,
  colorCard: colorCard,
  compositionBoxWidth: 816,
  containerMaxWidth: 412,
  pageMaxWidth: 1208,
  centred: "0 auto",
  border: "1px solid",
  shape: {
    borderRadius: variables.common.borderRadius,
  },
  palette: {
    primary: variables.colors.primary,
    secondary: variables.colors.secondary,
    error: variables.colors.error,
    text: variables.colors.text,
    backgroundSecondary: {
      default: variables.colors.backgroundHeader,
      paper: variables.colors.selectedBackground,
    },
    background: {
      default: variables.colors.background,
      paper: "#FFF",
    },
    divider: variables.colors.divider,
    grey: {
      400: variables.colors.border,
    },
    disabledColor: variables.colors.placeholder,
    info: variables.colors.info,
  },
  typography: {
    fontFamily: getFontFamily("Polestar"),
    color: variables.colors.text.primary,
    h1: {
      fontSize: "1.75rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.primary,
      [theme.breakpoints.up("md")]: {
        fontSize: "2rem",
        lineHeight: "2.5rem",
      },
    },
    h2: {
      fontSize: "1.75rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.primary,
      [theme.breakpoints.up("md")]: {
        fontSize: "2rem",
        lineHeight: "2.5rem",
      },
    },
    h3: {
      fontSize: "1.75rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.primary,
      [theme.breakpoints.up("md")]: {
        fontSize: "2rem",
        lineHeight: "2.5rem",
      },
    },
    h4: {
      color: variables.colors.text.primary,
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
    },
    h5: {
      color: variables.colors.text.primary,
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
    },
    // helper text
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
    button: {
      fontSize: "1rem",
      fontWeight: "normal" as "normal",
      lineHeight: "1.5rem",
      minHeight: 48,
    },
    subtitle2: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
      fontWeight: "normal" as "normal",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
    },
    body2: {
      fontSize: "1.2rem",
      lineHeight: "1.5rem",
      color: variables.colors.primary.main,
    },
  },
  radioButton: {
    borderRadius: variables.common.borderRadius,
    border: `1px solid ${variables.colors.border}`,
    activeBorder: `1px solid ${variables.colors.primary.main}`,
    activeBackgroundColor: "#FFFFFF",
    activeFontWeight: 700,
  },
  addOnCard: {
    addTextSelected: {
      color: variables.colors.contrastColor,
    },
    addText: {
      color: variables.colors.contrastColor,
    },
    component: {
      width: 380,
      display: "inline-block",
      marginBottom: "8px",
    },
    card: {
      border: `1px solid ${variables.colors.border}`,
      borderRadius: variables.common.borderRadius,
      padding: "25px 34px",
    },
    cardSelected: {
      border: `1px solid ${variables.colors.primary.main}`,
      padding: "25px 34px",
      backgroundColor: "#FFFFFF",
      addText: {
        color: variables.colors.contrastColor,
      },
    },
    header: {
      textColor: variables.colors.text.primary,
      fontWeight: 700,
    },
    price: {
      color: variables.colors.text.primary,
    },
  },
  checkoutLinks: {
    color: variables.colors.primary.main,
  },
  popup: {
    root: {
      fontFamily: getFontFamily("Polestar"),
      backgroundColor: "#FFF",
      "& p": {
        color: "#0F0F0F",
      },
    },
    h4: {
      color: "#0F0F0F",
      fontWeight: 700,
    },
  },
  uspContainerBlockWrapper: {
    backgroundColor: variables.colors.primary.main,
  },
  uspBlock: {
    header: {
      fontWeight: 700,
      color: "#FFFFFF",
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      fontFamily: getFontFamily("Polestar"),
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    text: {
      fontWeight: 400,
      color: "#FFFFFF",
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
  },
  overrides: {
    // Style sheet name. Look for specific component CSS API: https://material-ui.com/api/button/#css
    MuiTypography: {
      root: {
        textTransform: "none" as TextTransformProperty,
      },
    },
    MuiButton: {
      root: {
        textTransform: "none" as TextTransformProperty,
        minWidth: 162,
        width: "100%",
      },
      label: {
        justifyContent: "center",
      },
      fullWidth: {
        width: "100%",
        minWidth: 162,
      },
      contained: {
        boxShadow: props.buttons.boxShadow,
        "& .MuiButton-label": {
          "&::after": {
            content: '"\u2794"',
            color: variables.colors.contrastColor,
            fontSize: 20,
            marginLeft: 8,
          },
        },
        "&:hover": {
          boxShadow: props.buttons.boxShadow,
        },
        "&:active": {
          boxShadow: props.buttons.boxShadow,
        },
        "&$focusVisible": {
          boxShadow: props.buttons.boxShadow,
        },
        "a&": {
          "&:hover": {
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
          },
          "&:active": {
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
          },
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.main,
        },
      },
      containedPrimary: {
        fontWeight: 400,
      },
      outlinedSecondary: {
        "&:hover": {
          border: "1px solid " + variables.colors.secondary.dark,
          color: variables.colors.secondary.dark,
          backgroundColor: variables.colors.transparent,
        },
        "&:active": {
          border: "1px solid " + variables.colors.text.hint,
          color: variables.colors.text.hint,
          backgroundColor: variables.colors.transparent,
        },
        color: variables.colors.text.primary,
        border: "1px solid " + variables.colors.text.primary,
      },
      text: {
        "&:hover": {
          color: variables.colors.secondary.dark,
          backgroundColor: variables.colors.transparent,
        },
        "&:active": {
          color: variables.colors.text.hint,
          backgroundColor: variables.colors.transparent,
        },
        color: variables.colors.text.primary,
      },
      textPrimary: {
        "&:hover": {
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
        },
        "&:active": {
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
        },
        color: variables.colors.primary.main,
      },
    },
    MuiRadio: {
      root: {
        color: variables.colors.contrastColor,
      },
      colorSecondary: {
        "&.Mui-checked": {
          color: variables.colors.contrastColor,
        },
      },
    },
    MuiIconButton: {
      root: {
        color: variables.colors.primary.main,
        "&:hover": {
          color: variables.colors.primary.dark,
        },
      },
      sizeSmall: {
        padding: 0,
      },
    },
    MuiFormLabel: {
      root: {
        color: variables.colors.text.primary,
        "&$focused": {
          color: variables.colors.border,
        },
        fontWeight: 400,
      },
    },
    MuiInputLabel: {
      formControl: {
        color: variables.colors.text.primary,
        position: "relative" as PositionProperty,
        transform: "none",
        fontWeight: 400,
        lineHeight: "1.75rem", // safe to use InputLabel + <SvgIcon size='small' />
      },
    },
    MuiOutlinedInput: {
      root: {
        borderBottom: "1px solid " + variables.colors.border,
        borderRadius: 0,
        backgroundColor: variables.colors.background,
        "&:hover": {
          "& $notchedOutline": {
            borderColor: variables.colors.border,
          },
          "&$focused $notchedOutline": {
            borderColor: variables.colors.primary.main,
          },
          "&$error $notchedOutline": {
            borderColor: variables.colors.error.main,
          },
        },
        "&$focused $notchedOutline": {
          borderColor: variables.colors.primary.main,
          borderWidth: 0,
        },
      },
      notchedOutline: {
        borderColor: variables.colors.border,
        borderWidth: 0,
      },
      input: {
        "&::-webkit-input-placeholder": props.inputs.placeholder,
        "&::-moz-placeholder": props.inputs.placeholder, // Firefox 19+
        "&:-ms-input-placeholder": props.inputs.placeholder, // IE 11
        "&::-ms-input-placeholder": props.inputs.placeholder, // Edge
      },
    },
    MuiFormHelperText: {
      root: {
        color: variables.colors.text.hint,
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiListItem: {
      ...props.listItems,
      button: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
        "&$selected": {
          backgroundColor: variables.colors.background,
          "&:hover": {
            backgroundColor: variables.colors.hover,
          },
        },
      },
    },
    MuiMenuItem: {
      ...props.listItems,
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: variables.colors.transparent,
        },
        // Setting icon with IconComponent property, looses all select styling, so we change SVG path directly to one we need
        "&~ svg": {
          "&> path": {
            d: "path('M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z')",
          },
        },
      },
      icon: {
        fill: variables.colors.primary.dark,
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiSvgIcon: {
      fontSizeInherit: {
        fontSize: "56px",
      },
    },
    MuiLink: {
      underlineHover: {
        "a&": {
          // to increase specificy
          color: variables.colors.text.primary,
          textDecoration: "underline",
          "&:hover": {
            color: variables.colors.hover,
            textDecoration: "underline",
          },
          "&:active": {
            color: variables.colors.primary.main,
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.root,
        borderRadius: variables.common.borderRadius,
      },
      groupedHorizontal: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.groupedHorizontal,
        ...ToggleButtons.SetMuiToggleButtonGroupBorders({
          ...theme,
          shape: {
            borderRadius: variables.common.borderRadius,
          },
        }),
      },
    },
    MuiToggleButton: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButton.root,
        color: variables.colors.placeholder,
        border: "1px solid " + variables.colors.border,
        fontWeight: 400,
        "&$selected": {
          color: variables.colors.primary.main,
          fontWeight: 700,
          backgroundColor: "#FFFFFF",
          borderColor: variables.colors.primary.main,
          borderWidth: 2,
          "&:hover": {
            backgroundColor: variables.colors.hover,
          },
        },
        "&:hover": {
          backgroundColor: variables.colors.hover,
        },
      },
    },
    MuiPickersBasePicker: {
      container: {
        ...DatePicker.CommonBase.MuiPickersBasePicker.container,
      },
    },
    MuiCard: {
      root: {
        width: 385,
      },
    },
  },
  footerBar: {
    backgroundColor: variables.colors.primary.main,
    textColor: variables.colors.secondary.contrastText,
  },
  priceText: {
    totalPriceColor: variables.colors.text.primary,
    fontWeight: 700,
  },
  header: {
    "& *": {
      color: variables.colors.primary.main,
    },
  },
  outlinedButton: {
    "& span": {
      color: variables.colors.text.primary,
      width: "100%",
      height: "100%",
      minHeight: "56px",
    },
    "& span:hover": {
      color: variables.colors.primary.dark,
    },
    padding: "0",
    minHeight: "56px",
    display: "flex",
  },
  icons: {
    addIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M12.2783 12.2786L23.0349 12.2786V11.7214L12.2783 11.7214V0.964892L11.7211 0.964891L11.7211 11.7214H0.964635V12.2786H11.7211L11.7211 23.0351L12.2783 23.0351V12.2786Z'
          fill='#FF7500'
          stroke='#FF7500'
          stroke-width='0.5'
        />
      </svg>
    ),
    removeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M4.89763 20L4 19.1024L11.1024 12L4 4.89763L4.89763 4L12 11.1024L19.1024 4L20 4.89763L12.8976 12L20 19.1024L19.1024 20L12 12.8976L4.89763 20Z'
          fill='#FF7500'
        />
      </svg>
    ),
    selectIcon: <path d='M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z' />,
    closeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M4 19.0797L11.0945 11.9852L4 4.89013L4.89073 4L11.9852 11.0945L19.0797 4L19.9704 4.86054L12.8759 11.9852L20 19.1093L19.1099 20L11.9852 12.8753L4.89073 19.9704L4 19.0797Z'
          fill='#1A1A1A'
        />
      </svg>
    ),
    helpIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M11.4444 17H12.5556V10.8889H11.4444V17ZM12 9.30778C12.1937 9.30778 12.3561 9.24222 12.4872 9.11111C12.6183 8.98 12.6839 8.81759 12.6839 8.62389C12.6839 8.43019 12.6183 8.26778 12.4872 8.13667C12.3561 8.00574 12.1937 7.94028 12 7.94028C11.8063 7.94028 11.6439 8.00574 11.5128 8.13667C11.3817 8.26778 11.3161 8.43019 11.3161 8.62389C11.3161 8.81759 11.3817 8.98 11.5128 9.11111C11.6439 9.24222 11.8063 9.30778 12 9.30778ZM12.0036 22C10.6208 22 9.32074 21.7376 8.10333 21.2128C6.88611 20.688 5.82722 19.9757 4.92667 19.0761C4.02611 18.1765 3.31324 17.1185 2.78806 15.9022C2.26269 14.6861 2 13.3866 2 12.0036C2 10.6208 2.26241 9.32074 2.78722 8.10333C3.31204 6.88611 4.02426 5.82722 4.92389 4.92667C5.82352 4.02611 6.88148 3.31324 8.09778 2.78806C9.31389 2.26269 10.6134 2 11.9964 2C13.3792 2 14.6793 2.26241 15.8967 2.78722C17.1139 3.31204 18.1728 4.02426 19.0733 4.92389C19.9739 5.82352 20.6868 6.88148 21.2119 8.09778C21.7373 9.31389 22 10.6134 22 11.9964C22 13.3792 21.7376 14.6793 21.2128 15.8967C20.688 17.1139 19.9757 18.1728 19.0761 19.0733C18.1765 19.9739 17.1185 20.6868 15.9022 21.2119C14.6861 21.7373 13.3866 22 12.0036 22ZM12 20.8889C14.4815 20.8889 16.5833 20.0278 18.3056 18.3056C20.0278 16.5833 20.8889 14.4815 20.8889 12C20.8889 9.51852 20.0278 7.41667 18.3056 5.69444C16.5833 3.97222 14.4815 3.11111 12 3.11111C9.51852 3.11111 7.41667 3.97222 5.69444 5.69444C3.97222 7.41667 3.11111 9.51852 3.11111 12C3.11111 14.4815 3.97222 16.5833 5.69444 18.3056C7.41667 20.0278 9.51852 20.8889 12 20.8889Z'
          fill='#101820'
        />
      </svg>
    ),
    checkmarkIconCSS: encodeURIComponent(`<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2ZM12 20.75C7.1875 20.75 3.25 16.8125 3.25 12C3.25 7.1875 7.1875 3.25 12 3.25C16.8125 3.25 20.75 7.1875 20.75 12C20.75 16.8125 16.8125 20.75 12 20.75Z" fill="#101820" />
    <path d="M18.25 8.8125L17.3751 7.9375L10.125 15.1875L6.62494 11.6875L5.75 12.5625L10.125 16.9375L18.25 8.8125Z" fill="#101820" />
</svg>`),
    checkmarkIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2ZM12 20.75C7.1875 20.75 3.25 16.8125 3.25 12C3.25 7.1875 7.1875 3.25 12 3.25C16.8125 3.25 20.75 7.1875 20.75 12C20.75 16.8125 16.8125 20.75 12 20.75Z'
          fill='#101820'
        />
        <path d='M18.25 8.8125L17.3751 7.9375L10.125 15.1875L6.62494 11.6875L5.75 12.5625L10.125 16.9375L18.25 8.8125Z' fill='#101820' />
      </svg>
    ),
    statusIndicatorIcon: <circle cx='6' cy='6' r='5' fill={variables.colors.info.main} stroke='white' strokeWidth='2' />,
  },
  messageBox: {
    backgroundColor: "#E3E9F5",
    borderColor: variables.colors.transparent,
    dividerColor: variables.colors.border,
    textAlign: "left",
  },
  coverageSelect: {
    root: {
      CoverageHeaderName: {
        color: variables.colors.text.primary,
        fontWeight: 700,
      },
      CoverageItemText: {
        color: variables.colors.text.primary,
      },
    },
    selected: {
      CoverageHeaderName: {
        fontFamily: getFontFamily("Polestar"),
      },
      CoverageHeaderPrice: {
        fontFamily: getFontFamily("Polestar"),
      },
      textColor: variables.colors.text.primary,
      paddingBottom: 0,
      borderWidth: 2,
    },
    descriptionCard: {
      backgroundColor: variables.colors.selectedBackground,
      borderColor: variables.colors.selectedBackground,
      textColor: variables.colors.text.primary,
    },
  },
  CheckoutPage: {
    priceText: {
      color: variables.colors.text.primary,
    },
    bottomTotalPriceText: {
      color: variables.colors.text.primary,
    },
  },
  ConfirmationPage: {
    ConfirmationButtonOutlined: {
      color: variables.colors.primary.main,
    },
    ConfirmationButtonContained: {
      color: variables.colors.primary.contrastText,
      "&:hover": {
        color: variables.colors.primary.contrastText,
      },
    },
  },
  confirmationPageHeader: {
    color: variables.colors.text.primary,
  },
  skeletonHeaderText: {
    background: theme.palette.secondary.contrastText,
  },
  paymentOption: {
    label: {
      lineHeight: "1.75rem",
      color: variables.colors.text.primary,
    },
    color: variables.colors.text.primary,
    selectedPayment: {
      borderWidth: 1,
    },
    selectButton: {
      "& span": {
        color: variables.colors.contrastColor,
      },
    },
  },
  payment: {
    backgroundColor: "#FFFFFF",
  },
  statusIndicator: {
    borderRadius: 4,
  },
  stickyPriceButton: {
    backgroundColor: variables.colors.primary.main,
    hoverColor: variables.colors.primary.dark,
    borderColor: variables.colors.primary.main,
    textColor: variables.colors.primary.contrastText,
  },
};

export default createTheme(polestar);
